import {
  Scene,
  PerspectiveCamera,
  WebGLRenderer,
  DirectionalLight,
  AmbientLight,
  PCFSoftShadowMap,
  Clock,
  ACESFilmicToneMapping,
  EquirectangularReflectionMapping,
  SRGBColorSpace,
  PointLight,
  Texture,
  Color,
  SpotLight,
  MeshStandardMaterial,
  Mesh,
  SphereGeometry,
  Vector2,
  Plane,
  Vector3,
  DataTexture,
} from "three";

import eventBus from "./event_bus";

import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { RGBELoader } from "three/examples/jsm/loaders/RGBELoader";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";

import PoolBorders from "./pool_components/pool_borders";
import WoodPlanksOutline from "./pool_components/woodplank_outlines";
import WaterPlane from "./pool_components/water_plane";
import Pool from "./pool_components/pool";
import PoolBeach from "./pool_components/pool_beach";
import PondFilter from "./pool_components/pond_filter";
import PoolStairs from "./pool_components/pool_stairs";
import GardenOutline from "./pool_components/garden_outline";
import PoolProps from "./pool_components/pool_props";
import Spots from "./pool_components/spots";

import skyDay from "url:./textures/sky6.hdr";
import skyNight from "url:./textures/sky-night.hdr";
import AssetManager from "./utils/asset_manager";
import InfinityBorders from "./pool_components/inifinity_border";
import BoxWithBottomOrigin from "./utils/box_bottom_origin";
import Stats from "three/examples/jsm/libs/stats.module.js";
import { start } from "repl";

/////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////

class SceneManager {
  skyScene: Scene;
  scene: Scene;
  camera: PerspectiveCamera;
  renderer: WebGLRenderer;

  sunLight: DirectionalLight;
  causticSpot: SpotLight;
  ambientLight: AmbientLight;
  clock = new Clock();
  poolType = "std"; // infinity , edgeless, std, pond

  private poolBorders: PoolBorders;
  private woodPlanksOutline: WoodPlanksOutline;
  private gardenOutline: GardenOutline;
  private waterPlane: WaterPlane;
  private pool: Pool;
  private poolBeach: PoolBeach;
  private poolStairs: PoolStairs;
  private pondFilter: PondFilter;
  private spots: Spots;
  private poolProps: PoolProps;
  private infinityBorders: InfinityBorders;

  private poolLength: number;
  private poolWidth: number;
  private poolDepth: number;
  margelleHeight: number;

  private stats: Stats;
  private composer: EffectComposer;
  private bloomPass: UnrealBloomPass;
  isInEditMode: boolean = false;

  constructor() {
    this.setupScenes();
    this.initEventBusListeners();

    this.poolLength = 7;
    this.poolWidth = 3;
    this.poolDepth = 1.5;
    this.margelleHeight = 0.05;

    // Adding a Directional Light
    this.setupLights();
    // Adding a camera
    this.setupCanvasAndRenderer();

    // Pool Borders Setup
    this.poolBorders = new PoolBorders(
      this.poolLength,
      this.poolWidth,
      this.poolDepth,
      this.margelleHeight,
      this.poolType,
      0.2,
      this
    );
    this.poolBorders.addToScene(this.scene);
    this.poolBorders.setTimeOfDay("day");

    this.infinityBorders = new InfinityBorders(this.poolBorders, 0.05);
    //this.infinityBorders.addToScene(this.scene);

    // WoodPlanksOutline Setup
    this.woodPlanksOutline = new WoodPlanksOutline(this.poolBorders, 3.5);
    this.woodPlanksOutline.addToScene(this.scene);

    // Garden Outline Setup
    this.gardenOutline = new GardenOutline(
      this.woodPlanksOutline,
      this.poolBorders,
      1.2
    );
    this.gardenOutline.addToScene(this.scene);

    // WaterPlane Setup
    this.waterPlane = new WaterPlane(this.poolBorders, this.poolType, this);
    this.waterPlane.addToScene(this.scene);

    // Pool Setup
    this.pool = new Pool(this.poolLength, this.poolWidth, this.poolDepth, this);
    this.pool.addToScene(this.scene);

    this.pondFilter = new PondFilter(
      this.poolLength,
      this.poolWidth,
      this.poolDepth,
      this.scene
    );
    this.pondFilter.resize(
      this.poolLength,
      this.poolWidth,
      this.poolDepth,
      this.scene
    );
    this.pondFilter.setVisible(false);
    this.setupControls();

    // PoolBeach Setup
    this.poolBeach = new PoolBeach(
      this.poolLength,
      this.poolWidth,
      this.poolDepth
    );
    this.poolBeach.resize(
      this.poolLength,
      this.poolWidth,
      this.poolDepth,
      this.scene
    );

    // PoolStairs Setup
    this.poolStairs = new PoolStairs(
      this.poolBeach,
      this.poolLength,
      this.poolWidth,
      this.poolDepth,
      true,
      1
    );
    this.poolStairs.addToScene(this.scene);

    // Spots Setup
    this.spots = new Spots(this.poolLength, this.poolWidth, this.poolDepth);
    this.spots.init();

    // Pool Props
    this.poolProps = new PoolProps(this.scene);
    window.addEventListener("resize", this.handleWindowResize.bind(this));

    const ball = new SphereGeometry(0.8, 32, 32);
    const ballMat = new MeshStandardMaterial({ color: 0x0000ff });
    const ballMesh = new Mesh(ball, ballMat);
    ballMesh.layers.set(2);
    ballMesh.position.set(0, 1, 0);
    //this.scene.add(ballMesh);
    this.camera.layers.enable(1);
    this.camera.layers.enable(2);
  }

  private setupControls() {
    const controls = new OrbitControls(this.camera, this.renderer.domElement);
    controls.target.set(0, 0, 0);
    controls.minPolarAngle = 0.0;
    controls.maxPolarAngle = Math.PI / 2.5;
    controls.minDistance = 2;
    controls.maxDistance = 10;
    controls.update();
  }

  private handleWindowResize() {
    // Update the camera's aspect ratio and projection matrix
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();

    // Update the renderer's size
    this.renderer.setSize(window.innerWidth, window.innerHeight);
  }

  private setupScenes() {
    this.scene = new Scene();

    this.stats = new Stats();
    // document.body.appendChild(this.stats.dom);

    const refScene = this.scene;
    const tex = AssetManager.getInstance().getAsset("sky_day") as Texture;
    tex.mapping = EquirectangularReflectionMapping;
    refScene.environment = tex;
    refScene.background = tex;
  }

  private setupCanvasAndRenderer() {
    const aspectRatio = window.innerWidth / window.innerHeight;
    this.camera = new PerspectiveCamera(45, aspectRatio, 0.1, 1000);
    this.camera.position.z = -5;
    this.camera.position.x = -10;
    this.camera.position.y = 7;

    // Initialize renderer
    this.renderer = new WebGLRenderer({
      antialias: true,
      powerPreference: "high-performance",
      precision: "lowp",
      preserveDrawingBuffer: true,
    });
    this.renderer.toneMapping = ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 0.5;

    this.renderer.outputColorSpace = SRGBColorSpace;
    // this.renderer.localClippingEnabled=true;
    //this.renderer.localClippingEnabled = true;
    //this.renderer.clippingPlanes = [new Plane( new Vector3( 0, -5.5, 0 ) )];

    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = PCFSoftShadowMap;
    this.renderer.setSize(window.innerWidth, window.innerHeight);
  }

  private initEventBusListeners() {
    eventBus.on("updateDimensions", ({ length, width, depth }) => {
      console.log("updateDimensions", length, width, depth);
      this.poolLength = length;
      this.poolWidth = width;
      this.poolDepth = depth;
      this.resizePool(this.margelleHeight);

      this.poolBeach.resize(
        this.poolLength,
        this.poolWidth,
        this.poolDepth,
        this.scene
      );
      this.poolStairs.resize(
        this.poolLength,
        this.poolWidth,
        this.poolDepth,
        this.scene
      );
      this.spots.onPoolSizeUpdate(
        this.poolLength,
        this.poolWidth,
        this.poolDepth,
        this.scene
      );

      this.gardenOutline.resize(this.scene);
      if (this.poolType == "pond")
        this.pondFilter.resize(
          this.poolLength,
          this.poolWidth,
          this.poolDepth,
          this.scene
        );
      if (this.poolType == "overflow" || this.poolType == "infinity")
        this.infinityBorders.resize(this.scene);
    });

    eventBus.on("updateColor", (color) => {
      console.log("updateColor", color);

      this.pool.updateColor(color, this.scene);
      this.poolBeach.updateColor(color);
      this.poolStairs.updateColor(color);
      this.pondFilter.updateColor(color);
    });

    const refScene = this.scene;

    eventBus.on("updateLightingMode", (lightingMode) => {
      console.log("updateLightingMode", lightingMode);
      this.gardenOutline.setTimeOfDay(lightingMode);
      this.poolBorders.setTimeOfDay(lightingMode);

      this.spots.onTimeOfDayChanged(lightingMode, this.scene);

      if (lightingMode == "day") {
        //this.bloomPass.strength = 0.2;
        this.ambientLight.color = new Color(0xffe9cd);
        this.ambientLight.intensity = 1.5;
        const tex = AssetManager.getInstance().getAsset("sky_day") as Texture;
        tex.mapping = EquirectangularReflectionMapping;
        refScene.environment = tex;
        refScene.background = tex;
        refScene.backgroundBlurriness = 0.0;
        this.renderer.toneMappingExposure = 0.5;
      } else {
        this.ambientLight.color = new Color(0x3081b4);
        //this.bloomPass.strength = 10.5;
        this.ambientLight.intensity = 1.3;
        const tex = AssetManager.getInstance().getAsset("sky_night") as Texture;
        tex.mapping = EquirectangularReflectionMapping;
        refScene.environment = tex;
        refScene.background = tex;
        this.renderer.toneMappingExposure = 0.5;
        this.scene.backgroundBlurriness = 0.1;
        // this.scene.backgroundIntensity=0.5;
        // this.scene.background=null;
      }

      this.sunLight.intensity = lightingMode == "day" ? 3 : 0;

      this.pool.setTimeOfDay(lightingMode);
    });

    eventBus.on("updateStairsWidthMode", (fixed) => {
      console.log("updateStairsWidthMode", fixed);
      this.poolStairs.resize(
        this.poolLength,
        this.poolWidth,
        this.poolDepth,
        this.scene,
        !fixed
      );
    });

    eventBus.on("updateStairType", (stairType) => {
      switch (stairType) {
        case "Pure":
          this.poolStairs.setAllStairsProperties(
            true,

            false,
            this.scene
          );
          break;

        case "Excellence":
          this.poolStairs.setAllStairsProperties(
            false,
            false,
            this.scene
          );

          break;

        case "Slash":
          this.poolStairs.setAllStairsProperties(true, true, this.scene);
          break;

        case "Floating":
          this.poolStairs.setAllStairsProperties(true, false, this.scene);
          break;
      }

      
    });

    eventBus.on("updateUseAngularStairs", (value) => {
      console.log("update use angular stairs", value);
      this.poolStairs.setUseAngularStairs(value, this.scene);
    });

    eventBus.on("updateUseFloatingStairs", (floating) => {
      console.log("update use floating stairs", floating);
      this.poolStairs.setUseFloatingStairs(floating, this.scene);
    });

    eventBus.on("updateNumSpots", (value) => {
      console.log("update spots", value);
      this.spots.updateAmount(value, this.scene);
    });

    eventBus.on("updatePoolType", (poolType) => {
      if (poolType == "infinity") {
        this.margelleHeight = 0.25;
      } else {
        this.margelleHeight = 0.05;
      }

      if (poolType == "overflow" && this.poolType != "overflow") {
        this.infinityBorders.addToScene(this.scene);
        this.infinityBorders.resize(this.scene);
      } else {
        this.infinityBorders.removeFromScene(this.scene);
      }

      if (poolType == "infinity" && this.poolType != "infinity") {
        this.infinityBorders.addToScene(this.scene);
        this.infinityBorders.resize(this.scene);
      } else {
        this.infinityBorders.removeFromScene(this.scene);
      }

      this.poolBorders.resize(
        this.poolLength,
        this.poolWidth,
        this.poolDepth,
        this.margelleHeight,
        this.scene
      );

      if (poolType == "pond") {
        console.log("updatePoolType", poolType);
        this.pondFilter.setVisible(true);
      } else {
        this.pondFilter.setVisible(false);
      }

      if (poolType == "pond")
        this.pondFilter.resize(
          this.poolLength,
          this.poolWidth,
          this.poolDepth,
          this.scene
        );
      if (poolType == "overflow") this.infinityBorders.resize(this.scene);

      this.poolType = poolType;
      this.waterPlane.changePoolType(this.scene, poolType);
      this.poolBorders.changePoolType(this.scene, poolType);
      console.log("updatePoolType", poolType);
      this.pool.resize(
        this.poolLength,
        this.poolWidth,
        this.poolDepth,
        this.scene
      );
    });

    eventBus.on("updateResizeMode", (isEditMode: boolean) => {
      if (isEditMode == this.isInEditMode) return;

      this.isInEditMode = isEditMode;

      this.gardenOutline.setEditMode(isEditMode, this.scene);
      this.poolStairs.setEditMode(isEditMode, this.scene);
      this.poolBeach.setEditMode(isEditMode, this.scene);
      this.waterPlane.setEditMode(isEditMode, this.scene);
      this.pool.setEditMode(isEditMode, this.scene);
      //this.
    });

    eventBus.on("updateUseBeach", (poolBeach) => {
      console.log("updatePoolBeach", poolBeach);
      this.poolBeach.toggle(poolBeach, this.scene, this.poolLength, this.poolWidth, this.poolDepth);
      this.poolStairs.resize(
        this.poolLength,
        this.poolWidth,
        this.poolDepth,
        this.scene
      );
    });

    eventBus.on("updateFloatingStairs", (poolCover) => {
      console.log("updateFloatingStairs", poolCover);
      this.poolStairs.setUseFloatingStairs(poolCover, this.scene);
    });
  }

  private setupLights() {
    this.ambientLight = new AmbientLight(0xffe9cd, 1.5);
    this.scene.add(this.ambientLight);

    this.sunLight = new DirectionalLight(0xffffff, 1);
    this.sunLight.castShadow = true;

    this.sunLight.shadow.blurSamples = 16;
    this.sunLight.shadow.autoUpdate = true;
    this.sunLight.shadow.mapSize.width = 2048; // default is 512
    this.sunLight.shadow.mapSize.height = 2048; // default is 512
    this.sunLight.shadow.camera.near = 1; // default is 0.5
    this.sunLight.shadow.camera.far = 50; // default is 500
    this.sunLight.shadow.camera.left = -10;
    this.sunLight.shadow.camera.right = 10;
    this.sunLight.shadow.camera.top = 10;
    this.sunLight.shadow.camera.bottom = -10;
    this.sunLight.shadow.radius = 0.5;
    this.sunLight.position.set(15, 20, 10);
    this.sunLight.target.position.set(0, 0, 0);
    this.sunLight.intensity = 5.0;
    this.sunLight.castShadow = true;

    this.sunLight.shadow.bias = -0.0001;
    this.scene.add(this.sunLight.target);
    this.sunLight.lookAt(this.sunLight.target.position);
    this.scene.add(this.sunLight);
  }

  render() {
    const t = this.clock.getElapsedTime();
    this.poolBorders.updateTime(t);
    this.pool.updateTime(t);
    this.woodPlanksOutline.update(t);
    this.renderer.render(this.scene, this.camera);

    //this.stats.update();
  }

  private resizePool(margelleHeight = 0.5) {
    console.log("resize pool");
    if (this.poolType == "infinity") {
      this.margelleHeight = 0.25;
    } else {
      this.margelleHeight = 0.05;
    }

    this.poolBorders.resize(
      this.poolLength,
      this.poolWidth,
      this.poolDepth,
      this.margelleHeight,
      this.scene
    );

    this.woodPlanksOutline.resize(this.scene);
    this.waterPlane.resize(this.scene);

    this.pool.resize(
      this.poolLength,
      this.poolWidth,
      this.poolDepth,
      this.scene
    );
  }

  getFinalShot() {
    this.renderer.render(this.scene, this.camera);
    return this.renderer.domElement.toDataURL("image/jpeg", 0.75);
  }
}

export default SceneManager;
